import type { TFunction } from 'i18next';
import { DateValue, parseDate } from '@internationalized/date';
import { format as formatFNS } from 'date-fns';
import { addDays } from 'date-fns';
import { searchProviders } from '@provider-directory/app/actions/search';
import { titleCaseFromSlug } from '@provider-directory/functions/string';
import {
  DEFAULT_SEARCH_PARAMS,
  LocationCachedSlotsResource,
  ProviderDateCachedSlotsResource,
  ProviderResource,
} from '@provider-directory/types/providers.contracts';
import type { AppointmentType } from '@provider-directory/types/runtime-config.contracts';
import { ProviderTimeSlot } from '@provider-directory/types/scheduling.contracts';
export interface DaySlot {
  date: string;
  slotsNumber: number;
}

export interface NearestSlot {
  date: string;
  slotsCount: number;
  locationId: string;
}

export type LocationAvailability<SlotType> = Record<
  string,
  Record<string, SlotType>
>;

export type SingleProviderAvailability<SlotType> = Partial<
  Record<string, LocationAvailability<SlotType>>
>;

// V2 Types for location-based availability mapping
// Slots organized by location, then by date
export type LocationBasedAvailability = Partial<
  Record<string, Partial<Record<string, ProviderTimeSlot[]>>>
>;

export type FullProviderAvailability = LocationBasedAvailability;

export type GroupedCachedSlotsByLocation = Record<
  string,
  Record<string, number>
>;

export const groupCachedSlotsByLocation = (
  schedule?: LocationCachedSlotsResource[],
): GroupedCachedSlotsByLocation => {
  if (!schedule) return {};

  return schedule.reduce<GroupedCachedSlotsByLocation>((acc, location) => {
    const { departmentId, days } = location;
    const deptSlots = (acc[departmentId] ??= {});

    days.forEach((day: ProviderDateCachedSlotsResource) => {
      deptSlots[day.date] = day.slots;
    });

    return acc;
  }, {});
};

export const providerHasSupportedVisitTypes = (
  visitTypes: ProviderResource['visitTypes'],
  appointmentTypes: AppointmentType[],
): boolean =>
  visitTypes.some((visitType) =>
    appointmentTypes.some((supportedType) =>
      visitType.id.includes(supportedType.visitTypeId),
    ),
  );

export const generateEmptySlots = (
  startDate: Date,
  endDate: Date,
): DaySlot[] => {
  const result: DaySlot[] = [];
  let currentDate = startDate;

  while (currentDate <= endDate) {
    result.push({
      date: formatFNS(currentDate, 'yyyy-MM-dd'),
      slotsNumber: 0,
    });
    currentDate = addDays(currentDate, 1);
  }

  return result;
};

export const getOpenSlotCounts = (
  cachedSlots: GroupedCachedSlotsByLocation | null | undefined,
  locationId: string,
): DaySlot[] | null => {
  const locationSlots = cachedSlots?.[locationId];
  if (!locationSlots) {
    return null;
  }

  return Object.entries(locationSlots).map(([date, slotsCount]) => ({
    date,
    slotsNumber: slotsCount,
  }));
};

export interface NearestAvailability {
  byLocation: Record<string, NearestSlot>;
  closestSlot: NearestSlot | null;
}

export const findNearestSlotsByLocation = (
  providerSlots: GroupedCachedSlotsByLocation,
): Record<string, NearestSlot> => {
  const byLocation: Record<string, NearestSlot> = {};

  Object.entries(providerSlots).forEach(([departmentId, dates]) => {
    const sortedDates = Object.keys(dates).sort(); // dates are YYYY-MM-DD
    if (sortedDates.length > 0) {
      const firstDate = sortedDates[0];
      byLocation[departmentId] = {
        date: firstDate,
        slotsCount: dates[firstDate],
        locationId: departmentId,
      };
    }
  });

  return byLocation;
};

export const getNearestAvailability = (
  providerSlots: GroupedCachedSlotsByLocation,
): NearestAvailability => {
  const byLocation = findNearestSlotsByLocation(providerSlots);

  let closestSlot: NearestSlot | null = null;
  Object.values(byLocation).forEach((slot) => {
    if (!closestSlot || slot.date < closestSlot.date) {
      closestSlot = slot;
    }
  });

  return { byLocation, closestSlot };
};

export const filterDatesByFirstStartDate = (
  slotsGroupedByLocation: GroupedCachedSlotsByLocation,
  startDate: DateValue,
): GroupedCachedSlotsByLocation => {
  const startDateParsed = parseDate(startDate.toString());

  const filtered: GroupedCachedSlotsByLocation = {};

  Object.entries(slotsGroupedByLocation).forEach(([locationId, dates]) => {
    const filteredDates: Record<string, number> = {};

    Object.entries(dates).forEach(([date, slotsCount]) => {
      const parsed = parseDate(date);
      if (parsed.compare(startDateParsed) >= 0) {
        filteredDates[date] = slotsCount;
      }
    });

    if (Object.keys(filteredDates).length > 0) {
      filtered[locationId] = filteredDates;
    }
  });

  return filtered;
};

const RFVDisplayMap: Partial<Record<string, string>> = {
  // Dermatology
  acne: 'ACNE',
  psoriasis: 'PSORIASIS TREATMENT',
  cosmetics: 'COSMETIC',
  'general-derm': 'GENERAL DERM',
  'treatment-for-skin-cancer': 'SKIN CANCER TREATMENT',
  'skin-check': 'SKIN CANCER SCREENING',

  // Allergy
  asthma: 'ASTHMA',
  'medication-allergies': 'MEDICATION ALLERGY',
  'food-allergies': 'FOOD ALLERGY',
  'general-allergy': 'GENERAL ALLERGY',
  immunology: 'IMMUNOLOGY',

  // Orthopedics
  'neck-spine-or-back': 'NECK/SPINE/BACK',

  // Shoulder
  'shoulder-fracture': 'SHOULDER FRACTURE',
  'shoulder-arthritis': 'SHOULDER ARTHRITIS',
  'shoulder-replacement': 'SHOULDER REPLACEMENT',
  'something-else-shoulder': 'SHOULDER ISSUE',

  // Arm / Elbow / Hand / Wrist
  'elbow-fracture': 'ELBOW FRACTURE',
  'hand-or-finger-fracture': 'HAND/FINGER FRACTURE',
  'wrist-fracture': 'WRIST FRACTURE',
  'elbow-pain-or-tennis-elbow': 'ELBOW PAIN',
  'carpal-tunnel': 'CARPAL TUNNEL',
  'wrist-injury-or-pain': 'WRIST ISSUE',
  'brachial-plexus-injury': 'BRACHIAL PLEXUS',
  'something-else-arm-elbow-hand-or-wrist': 'ARM/HAND/WRIST ISSUE',

  // Hip
  'hip-arthritis': 'HIP ARTHRITIS',
  'hip-replacement': 'HIP REPLACEMENT',
  'hip-issue': 'HIP ISSUE',

  // Knee
  'knee-pain': 'KNEE PAIN',
  'calf-pain': 'CALF PAIN',
  'knee-replacement': 'KNEE REPLACEMENT',
  'knee-fracture': 'KNEE FRACTURE',
  'something-else-knee': 'KNEE ISSUE',

  // Foot / Ankle
  'ankle-injury': 'ANKLE INJURY',
  'ankle-pain': 'ANKLE PAIN',
  'bunion-or-heel-spurs': 'BUNION/HEEL SPUR',
  'foot-care': 'FOOT CARE',
  'something-else-foot': 'FOOT ISSUE',

  // Sports Medicine
  'sports-medicine-consultation': 'SPORTS CONSULT',
  'leg-fracture': 'LEG FRACTURE',
  'hamstring-or-quadriceps-injury': 'HAMSTRING/QUAD',

  // Oncology
  oncology: 'ONCOLOGY',

  // Endocrinology – Diabetes Care
  'type-1-diabetes': 'TYPE 1 DIABETES',
  'type-2-diabetes': 'TYPE 2 DIABETES',
  'diabetes-unknown': 'DIABETES UNKNOWN',

  // Endocrinology – Thyroid Concerns
  'thyroid-cancer': 'THYROID CANCER',
  'thyroid-condition': 'THYROID',

  // Endocrinology – Weight Management
  'weight-management': 'WEIGHT MANAGEMENT',

  // Endocrinology – Other Hormone / Bone / Metabolism Concerns
  'adrenal-or-pituitary-gland-conditions': 'PITUITARY ADRENAL',
  'perimenopausal-or-postmenopausal-hormone-therapy': 'MENOPAUSE',
  'gender-affirming-hormone-therapy': 'GENDER CARE',
  'other-hormone-bone-or-metabolism-concerns': 'GENERAL ENDO',

  // Gastroenterology
  'general-digestive-problems-or-questions': 'GENERAL GI',
  'inflammatory-bowel-disease': 'IBD',

  // Pediatrics
  'sick-visit-under-18': 'SICK',
  'wellness-visit-under-18': 'WELLNESS',
  'office-visit-18-and-older': 'ESTABLISH CARE',
  'annual-physical-18-and-older': 'PHYSICAL',
};

interface FindNextSlotOptions {
  onlyLocationId?: string;
  excludeLocationId?: string;
}

export const findNextSlot = (
  cachedSlots: GroupedCachedSlotsByLocation,
  cutoffDate: string,
  options: FindNextSlotOptions = {},
): NearestSlot | null => {
  let nearestSlot: NearestSlot | null = null;

  for (const [locationId, dates] of Object.entries(cachedSlots)) {
    if (options.onlyLocationId && locationId !== options.onlyLocationId)
      continue;
    if (options.excludeLocationId && locationId === options.excludeLocationId)
      continue;

    for (const [date, count] of Object.entries(dates)) {
      if (date < cutoffDate || count <= 0) continue;

      if (!nearestSlot || date < nearestSlot.date) {
        nearestSlot = { date, slotsCount: count, locationId };
      }
    }
  }

  return nearestSlot;
};

export const getRFVLabel = (rfv: string | null): string | null => {
  if (!rfv) return null;

  const match = Object.keys(RFVDisplayMap).find((key) => rfv.startsWith(key));

  return match ? RFVDisplayMap[match]! : null;
};

const RFV_SUFFIX_RE =
  /(?:-no-medicaid|-medicaid|-18-49-yo|-18-49|-50-and-over)+$/;

export const removeRFVSuffix = (rfv: string): string =>
  rfv.replace(RFV_SUFFIX_RE, '');

export const getReasonForVisitLabel = (rfvId: string, t: TFunction): string => {
  const strippedKey = removeRFVSuffix(rfvId);
  return t(`decisionTree.reasonForVisit.${strippedKey}.label`, {
    defaultValue: titleCaseFromSlug(strippedKey),
  });
};

export const getAvailableProvidersForRFV = async (
  rfv: string,
): Promise<{
  availableProviders: ProviderResource[];
  totalProviders: number;
}> => {
  const response = await searchProviders({
    searchParams: {
      ...DEFAULT_SEARCH_PARAMS,
      reasonForVisit: rfv,
    },
  });
  const availableProviders = response.providers.filter(
    (provider) => (provider.schedule ?? []).length > 0,
  );
  return { availableProviders, totalProviders: response.pagination.total };
};
